import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import EnFilterButtonBase from 'Components/datagrid/filterButtons/EnFilterButtonBase.vue';
import EnAutocompleteComponent from 'Components/inputs/EnAutocomplete/EnAutocomplete.vue';
import VarType from 'Js/enums/varType';
import { FilterEvent } from '../types/filterEvent';
import { useFilter } from '../composables/filterButtonComposable';
import { useResetFilter } from '../composables/useFilterReset';
import { camelCaseToKebabCase } from 'Js/utils/stringUtils';
import { FilterType } from 'Js/enums/filterType.js';
const filterMenuOpenedEvent = 'en:filter-menu-opened';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnAutocompleteFilterButton',
    props: /*@__PURE__*/ _mergeModels({
        /**
         * String displayed as the hint of the text field.
         */
        hint: String,
        /**
         * The items available.
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * String displayed as the label of the text field.
         */
        label: String,
        /**
         * Specifies id for input.
         */
        id: String,
        /**
         * Property name or function that takes an item returned from the endpoint and returns the string that should be displayed.
         */
        displayExpression: [String, Function],
        /**
         * Either the name of a model property that is searched in, or a function that takes a search string and returns a devextreme filter in array form
         */
        searchExpression: [String, Function],
        /**
         * Either the value to be considered in the filter or a function altering the input value
         */
        searchValue: [String, Function],
        /**
         * A select expression in array form.
         */
        selectExpression: Array,
        /**
         * Property name used to identify unique items.
         * Defaults to 'Id'.
         */
        itemValue: String,
        /**
         * Validation rules for the text field.
         */
        rules: Array,
        /**
         * Uri to send the request to.
         */
        apiUri: String,
        /**
         * Function that is executed when value is set from url
         */
        onLoadingFromUrl: Function,
        /**
         * Used to get distinct values. Should always be used if the autocomplete uses the same api address.
         * If this is not set, and, for example, the first 10 results have the same enum value CustomerType, no more are loaded
         * and only this one value is offered in the dropdown selection of the autocomplete.
         */
        group: String,
        /**
         * Used with group. Remove value = null
         *
         */
        hideNullValues: {
            type: Boolean,
            default: false
        },
        /**
         * Used to preselect value
         */
        selectedValue: Object,
        /**
         * String that is displayed when filter is not active.
         */
        noFilterText: {
            type: String,
        },
        /**
         * Specifies whether autocomplete should use "contains" instead of equals
         * Defaults to false.
         */
        useContains: {
            type: Boolean,
            default: true,
        },
        filterType: String,
        itemsPerPage: {
            type: Number,
            default: 10,
        },
    }, {
        "modelValue": {},
        "modelModifiers": {},
        "openOnRender": {},
        "openOnRenderModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:filterType', 'update:modelValue', 'update:openOnRender', FilterEvent.FilterUpdated, FilterEvent.FilterReset], ["update:modelValue", "update:openOnRender"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const displayPrefix = ref(`${t('common.filters.contains')}  `);
        const hasDisplayPrefix = ref(false);
        const noFilterTextComputed = computed(() => {
            return props.noFilterText ?? t('common.filters.noFilterText');
        });
        const displayString = ref(noFilterTextComputed.value);
        let firstLoadPreselected = false;
        const preselectedValue = ref(props.selectedValue);
        const enAutocomplete = ref();
        const modelValue = _useModel(__props, 'modelValue');
        const openOnRender = _useModel(__props, 'openOnRender');
        const emit = __emit;
        const menuIsVisible = ref(openOnRender.value);
        const filterMenuInstanceId = `filter-menu-${Math.random().toString(36).slice(2)}`;
        watch(menuIsVisible, (newValue) => {
            emit('update:openOnRender', newValue);
            if (newValue) {
                window.dispatchEvent(new CustomEvent(filterMenuOpenedEvent, {
                    detail: { instanceId: filterMenuInstanceId }
                }));
            }
        });
        watch(openOnRender, (newValue) => {
            menuIsVisible.value = Boolean(newValue);
        });
        function onAnotherFilterMenuOpened(event) {
            if (event?.detail?.instanceId === filterMenuInstanceId) {
                return;
            }
            menuIsVisible.value = false;
        }
        const { resetFilter } = useResetFilter(emit);
        const currentOption = computed({
            get() {
                return modelValue.value;
            },
            set(value) {
                const newValue = value?.[props.itemValue] ?? value;
                emit('update:filterType', hasDisplayPrefix.value && props.useContains ? FilterType.Contains : FilterType.Equal);
                emit('update:modelValue', newValue);
                applyFilters(newValue);
            }
        });
        const { returnId, returnFilterButtonId, } = useFilter(props, { emit }, modelValue, updateDisplayString);
        const menuId = computed(() => {
            if (props.id)
                return 'en-filter-menu-' + camelCaseToKebabCase(props.id);
        });
        const hasAutofocus = computed(() => {
            return modelValue.value === undefined || modelValue.value === null;
        });
        const displayExpressionIsFunction = computed(() => {
            return typeof props.displayExpression === VarType.Function;
        });
        watch(() => modelValue, (value) => {
            if (value.value === undefined) {
                hasDisplayPrefix.value = false;
            }
            updateDisplayString(value.value);
        }, { deep: true });
        /**
         * With vue 3 component re-renders every time any prop is changed.
         * This leads to some bugs. To avoid them we use preselectedObject and selectedValue to show correct values for those autocomplete-s,
         * which use different value and text
         * @param value
         * @returns {*|{}|Object}
         */
        function getPreselectedObject(value) {
            if (typeof value === 'object') {
                return value;
            }
            const preselectedObject = {};
            // If prop or inner object is set use this value
            if (props.selectedValue && props.selectedValue[props.itemValue] === value) {
                return preselectedValue.value;
            }
            if (preselectedValue.value && preselectedValue.value[props.itemValue] === value) {
                return preselectedValue.value;
            }
            preselectedObject[props.itemValue] = value;
            // Trick to avoid exception 
            return props.items.find(i => i[props.itemValue] === value) ?? preselectedObject;
        }
        async function autocompleteRendered() {
            //If value is preselected and autocomplete is async
            if (firstLoadPreselected) {
                await enAutocomplete.value.loadItems(modelValue.value);
                // trigger change value manually
                enAutocomplete.value.selectedItem = modelValue.value;
                if (!enAutocomplete?.value?.selectedItemObject) {
                    menuIsVisible.value = false;
                }
            }
            firstLoadPreselected = false;
        }
        async function applyFilters(newValue) {
            await nextTick();
            // Obtain object either from child component (if manual) or from items (if preselected) 
            const selectedObject = (newValue !== null && newValue !== undefined)
                ? (enAutocomplete.value?.selectedItemObject ?? getPreselectedObject(newValue))
                : null;
            preselectedValue.value = selectedObject;
            updateDisplayString(newValue);
            menuIsVisible.value = false;
            emit(FilterEvent.FilterUpdated, selectedObject);
        }
        async function confirmWithEnter() {
            enAutocomplete.value?.forceSelection();
            await nextTick();
            const value = modelValue.value;
            emit('update:filterType', hasDisplayPrefix.value && props.useContains ? FilterType.Contains : FilterType.equal);
            await applyFilters(value);
        }
        function updateDisplayString(value) {
            displayString.value = getDisplayString(value);
        }
        function getDisplayString(value) {
            if (value === null || value === undefined) {
                return noFilterTextComputed.value;
            }
            if (!props.displayExpression)
                return value;
            // Obtain object either from child component (if manual) or from items (if preselected)
            const selectedObject = enAutocomplete.value?.selectedItemObject ?? getPreselectedObject(value);
            if (displayExpressionIsFunction.value) {
                return props.displayExpression(selectedObject);
            }
            if (typeof props.displayExpression === VarType.String) {
                return selectedObject[props.displayExpression];
            }
            console.error('could not handle display expression ' + props.displayExpression + ' for value ' + value);
            return value;
        }
        function blur() {
            menuIsVisible.value = false;
        }
        function focusAutocompleteInput() {
            enAutocomplete.value?.$el?.querySelector('input')?.focus();
        }
        onBeforeMount(async () => {
            if (modelValue.value && props.onLoadingFromUrl) {
                preselectedValue.value = await props.onLoadingFromUrl(modelValue.value);
                updateDisplayString(preselectedValue.value);
            }
            else {
                updateDisplayString(modelValue.value);
            }
            if (modelValue.value && props.filterType === FilterType.Contains) {
                hasDisplayPrefix.value = true;
            }
        });
        onMounted(() => {
            window.addEventListener(filterMenuOpenedEvent, onAnotherFilterMenuOpened);
        });
        onBeforeUnmount(() => {
            window.removeEventListener(filterMenuOpenedEvent, onAnotherFilterMenuOpened);
        });
        const __returned__ = { props, displayPrefix, hasDisplayPrefix, noFilterTextComputed, displayString, get firstLoadPreselected() { return firstLoadPreselected; }, set firstLoadPreselected(v) { firstLoadPreselected = v; }, preselectedValue, enAutocomplete, modelValue, openOnRender, emit, menuIsVisible, filterMenuInstanceId, filterMenuOpenedEvent, onAnotherFilterMenuOpened, resetFilter, currentOption, returnId, returnFilterButtonId, menuId, hasAutofocus, displayExpressionIsFunction, getPreselectedObject, autocompleteRendered, applyFilters, confirmWithEnter, updateDisplayString, getDisplayString, blur, focusAutocompleteInput, EnFilterButtonBase, EnAutocompleteComponent };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
